<!--
  OtpInput.vue (Vue 2)
  -------------------------------------------------------------
  A 6-digit (configurable) one-time-code input, ported from the React
  InputOTP usage. Renders one box per digit, auto-advances on typing,
  supports backspace, arrow keys, and pasting the full code at once.

  Props:
    - value (String) ...... the current code (v-model)
    - length (Number) ..... number of digits (default 6)
    - invalid (Boolean) ... show error styling
    - autofocus (Boolean) . focus the first box on mount

  Events:
    - input(code) ......... emitted on every change (v-model)
    - complete(code) ...... emitted once all digits are filled

  Dependencies: none
-->
<template>
  <div class="mm-otp" :class="{ 'mm-otp--invalid': invalid }">
    <input
      v-for="(d, i) in digits"
      :key="i"
      :ref="`box-${i}`"
      class="mm-otp__box"
      type="text"
      inputmode="numeric"
      autocomplete="one-time-code"
      maxlength="1"
      :value="d"
      @input="onInput(i, $event)"
      @keydown="onKeydown(i, $event)"
      @paste="onPaste"
      @focus="$event.target.select()"
    />
  </div>
</template>

<script>
export default {
  name: "OtpInput",
  props: {
    value: { type: String, default: "" },
    length: { type: Number, default: 6 },
    invalid: { type: Boolean, default: false },
    autofocus: { type: Boolean, default: false },
  },
  computed: {
    digits() {
      const out = [];
      for (let i = 0; i < this.length; i += 1) out.push(this.value[i] || "");
      return out;
    },
  },
  mounted() {
    if (this.autofocus) this.focusBox(0);
  },
  methods: {
    focusBox(i) {
      const ref = this.$refs[`box-${i}`];
      const el = Array.isArray(ref) ? ref[0] : ref;
      if (el) {
        el.focus();
        el.select();
      }
    },
    emit(code) {
      this.$emit("input", code);
      if (code.length === this.length && !code.includes(" ")) {
        this.$emit("complete", code);
      }
    },
    setChar(index, char) {
      const arr = [];
      for (let i = 0; i < this.length; i += 1) {
        arr.push(i === index ? char : this.value[i] || "");
      }
      return arr.join("").replace(/\s+$/, "");
    },
    onInput(i, e) {
      const raw = e.target.value.replace(/\D/g, "");
      const char = raw.slice(-1);
      e.target.value = char;
      const code = this.setChar(i, char);
      this.emit(code);
      if (char && i < this.length - 1) this.focusBox(i + 1);
    },
    onKeydown(i, e) {
      if (e.key === "Backspace") {
        if (!this.value[i] && i > 0) {
          e.preventDefault();
          const code = this.setChar(i - 1, "");
          this.emit(code);
          this.focusBox(i - 1);
        } else {
          const code = this.setChar(i, "");
          this.emit(code);
        }
      } else if (e.key === "ArrowLeft" && i > 0) {
        e.preventDefault();
        this.focusBox(i - 1);
      } else if (e.key === "ArrowRight" && i < this.length - 1) {
        e.preventDefault();
        this.focusBox(i + 1);
      }
    },
    onPaste(e) {
      e.preventDefault();
      const text = (e.clipboardData || window.clipboardData).getData("text");
      const code = text.replace(/\D/g, "").slice(0, this.length);
      if (!code) return;
      this.emit(code);
      this.$nextTick(() => {
        this.focusBox(Math.min(code.length, this.length - 1));
      });
    },
  },
};
</script>

<style scoped>
.mm-otp {
  display: flex;
  gap: 8px;
  justify-content: center;
}
.mm-otp__box {
  width: 44px;
  height: 52px;
  box-sizing: border-box;
  text-align: center;
  font-size: 20px;
  font-weight: 600;
  color: #002f42;
  border: 1px solid #d6dde1;
  border-radius: 8px;
  background: #fff;
  outline: none;
}
.mm-otp__box:focus {
  border-color: #00acee;
  box-shadow: 0 0 0 3px rgba(0, 172, 238, 0.15);
}
.mm-otp--invalid .mm-otp__box {
  border-color: #ee4200;
}
</style>



// WEBPACK FOOTER //
// src/components/create_account/OtpInput.vue